<script setup lang="ts">
import { HeartIcon } from '@heroicons/vue/24/solid'
import SnowflakeIcon from './SnowflakeIcon.vue'
import FlowerIcon from './FlowerIcon.vue'
import type { PromoTopLineTheme } from '@interfaces/promo.interface'

withDefaults(defineProps<{
    theme?: PromoTopLineTheme,
    message?: string,
}>(), {
    theme: 'default',
    message: undefined,
})
</script>

<template>
    <div class="h-[26px] overflow-hidden">
        <div
            v-if="theme === 'hearts'"
            class="flex h-full items-center justify-center bg-[#EC4F87] text-white"
        >
            <div class="relative font-medium text-p3">
                <HeartIcon class="absolute -top-0.5 w-4 h-4 rotate-[22deg] -left-6 fill-[#E88DAE]" />
                <HeartIcon class="absolute top-0.5 w-3.5 h-3.5 rotate-[-25deg] -left-[2.375rem] fill-[#E88DAE] opacity-80" />
                <HeartIcon class="absolute top-3.5 w-3 h-3 rotate-[22deg] -left-[1.75rem] fill-[#E88DAE] opacity-60" />
                <slot>
                    <span v-html="message"></span>
                </slot>
                <HeartIcon class="absolute -top-0.5 w-4 h-4 rotate-[-22deg] -right-6 fill-[#E88DAE]" />
                <HeartIcon class="absolute top-0.5 w-3.5 h-3.5 rotate-[25deg] -right-[2.375rem] fill-[#E88DAE] opacity-80" />
                <HeartIcon class="absolute top-3.5 w-3 h-3 rotate-[-22deg] -right-[1.75rem] fill-[#E88DAE] opacity-60" />
            </div>
        </div>

        <div
            v-else-if="theme === 'snowflakes'"
            class="flex h-full items-center justify-center bg-[#FF3A3A] text-white"
        >
            <div class="relative inset-0 font-medium text-p3">
                <SnowflakeIcon class="absolute top-1.5 w-[12px] h-[12px] -left-[9rem]" />
                <SnowflakeIcon class="absolute top-0 w-[18px] h-[18px] -left-[6.25rem]" />
                <SnowflakeIcon class="absolute top-2.5 w-[14px] h-[14px] -left-[3.75rem]" />
                <SnowflakeIcon class="absolute top-0.5 w-[18px] h-[18px] -left-[1.75rem]" />
                <slot>
                    <span v-html="message"></span>
                </slot>
                <SnowflakeIcon class="absolute top-0.5 w-[18px] h-[18px] -right-[1.75rem]" />
                <SnowflakeIcon class="absolute top-2.5 w-[14px] h-[14px] -right-[3.75rem]" />
                <SnowflakeIcon class="absolute top-0 w-[18px] h-[18px] -right-[6.25rem]" />
                <SnowflakeIcon class="absolute top-1.5 w-[12px] h-[12px] -right-[9rem]" />
            </div>
        </div>

        <div
            v-else-if="theme === 'flowers'"
            class="flex items-center justify-center h-full text-white bg-mint-500"
        >
            <div class="relative inset-0 font-medium text-p3">
                <FlowerIcon class="absolute top-2.5 w-[8px] h-[8px] -left-[4.25rem]" />
                <FlowerIcon class="absolute -top-px w-[10px] h-[10px] -left-[3.5rem]" />
                <FlowerIcon class="absolute top-2.5 w-[12px] h-[12px] -left-[3rem]" />
                <FlowerIcon class="absolute top-0.5 w-[18px] h-[18px] -left-[1.75rem]" />
                <slot>
                    <span v-html="message"></span>
                </slot>
                <FlowerIcon class="absolute top-0.5 w-[18px] h-[18px] -right-[1.75rem]" />
                <FlowerIcon class="absolute top-2.5 w-[12px] h-[12px] -right-[3rem]" />
                <FlowerIcon class="absolute -top-px w-[10px] h-[10px] -right-[3.5rem]" />
                <FlowerIcon class="absolute top-2.5 w-[8px] h-[8px] -right-[4.25rem]" />
            </div>
        </div>

        <div
            v-else-if="theme === 'black'"
            class="h-full text-center leading-[26px] bg-[#150508] text-white font-medium text-p3"
        >
            <slot>
                <span v-html="message"></span>
            </slot>
        </div>

        <div
            v-else
            class="h-full text-center leading-[26px] font-medium text-white bg-primary-600 text-p3"
        >
            <slot>
                <span v-html="message"></span>
            </slot>
        </div>
    </div>
</template>
